import {
  OrderStatus,
  OrderType,
  PaymentEventStage,
  PaymentEventStatus,
} from "@/api/payments";

const STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: "Pending",
  [OrderStatus.PAID]: "Paid",
  [OrderStatus.CANCELLED]: "Cancelled",
  [OrderStatus.FAILED]: "Failed",
  [OrderStatus.REVOKED]: "Revoked",
};

export const formatOrderStatus = (status: OrderStatus): string =>
  STATUS_LABELS[status] ?? status;

export const orderStatusChipColor = (
  status: OrderStatus,
): "success" | "error" | "warning" | "default" | "info" => {
  switch (status) {
    case OrderStatus.PAID:
      return "success";
    case OrderStatus.FAILED:
    case OrderStatus.CANCELLED:
      return "error";
    case OrderStatus.REVOKED:
      return "warning";
    case OrderStatus.PENDING:
      return "info";
    default:
      return "default";
  }
};

const TYPE_LABELS: Record<OrderType, string> = {
  [OrderType.GENERAL]: "One-time",
  [OrderType.RECURRING]: "Recurring",
  [OrderType.SPEND_PURCHASE]: "Spend",
};

export const formatOrderType = (type: OrderType): string =>
  TYPE_LABELS[type] ?? type;

/**
 * The label the admin actually wants to see for "what kind of purchase is
 * this." Derives from Order type + whether the product is recurring:
 *   - RECURRING type → "Renewal" (recurring-billing cron created it)
 *   - GENERAL + recurring product → "Subscription" (interactive sign-up
 *     for a recurring plan)
 *   - GENERAL + non-recurring → "One-time"
 *   - SPEND_PURCHASE → "Spend"
 */
export const formatPurchaseKind = (
  type: OrderType,
  isRecurringProduct: boolean,
): string => {
  if (type === OrderType.RECURRING) return "Renewal";
  if (type === OrderType.SPEND_PURCHASE) return "Spend";
  return isRecurringProduct ? "Subscription" : "One-time";
};

/**
 * Threshold after which a non-recurring PENDING Order is considered abandoned
 * for display purposes. No stored state — derived purely from age at render.
 */
const ABANDONED_AFTER_MS = 10 * 60 * 1000;

/**
 * Derives "what does the admin actually want to see" from the raw order +
 * latest-event state. Priorities (only when the order is still PENDING; for
 * terminal statuses the order's status wins so stale FAILED webhooks on PAID
 * orders don't mislabel them):
 *   1. PENDING + latest attempt FAILED -> "Failed" (most recent observable outcome).
 *   2. PENDING + stale non-recurring -> "Abandoned".
 *   3. Otherwise the raw order-status label.
 */
export const formatDisplayStatus = (
  status: OrderStatus,
  latestEventStatus?: PaymentEventStatus,
  createdAt?: string,
  type?: OrderType,
): string => {
  if (
    status === OrderStatus.PENDING &&
    latestEventStatus === PaymentEventStatus.FAILED
  ) {
    return "Failed";
  }
  if (
    status === OrderStatus.PENDING &&
    type !== OrderType.RECURRING &&
    createdAt &&
    Date.now() - new Date(createdAt).getTime() > ABANDONED_AFTER_MS
  ) {
    return "Abandoned";
  }
  return formatOrderStatus(status);
};

export const displayStatusChipColor = (
  status: OrderStatus,
  latestEventStatus?: PaymentEventStatus,
  createdAt?: string,
  type?: OrderType,
): "success" | "error" | "warning" | "default" | "info" => {
  if (
    status === OrderStatus.PENDING &&
    latestEventStatus === PaymentEventStatus.FAILED
  ) {
    return "error";
  }
  if (
    status === OrderStatus.PENDING &&
    type !== OrderType.RECURRING &&
    createdAt &&
    Date.now() - new Date(createdAt).getTime() > ABANDONED_AFTER_MS
  ) {
    return "warning";
  }
  return orderStatusChipColor(status);
};

const STAGE_LABELS: Record<PaymentEventStage, string> = {
  [PaymentEventStage.CHECKOUT_INIT]: "Checkout init",
  [PaymentEventStage.PRODUCT_UPDATED]: "Product updated",
  [PaymentEventStage.ENROLLMENT_CHECK]: "Enrollment check",
  [PaymentEventStage.ACS_CHALLENGE]: "ACS challenge",
  [PaymentEventStage.THREE_D_SALE]: "3D Sale",
  [PaymentEventStage.DIRECT_SALE]: "Direct Sale",
  [PaymentEventStage.RECURRING_CHARGE]: "Recurring charge",
  [PaymentEventStage.WEBHOOK_CONFIRM]: "Confirm webhook",
};

export const formatStage = (stage: PaymentEventStage): string =>
  STAGE_LABELS[stage] ?? stage;

export const eventStatusChipColor = (
  status: PaymentEventStatus,
): "success" | "error" | "warning" | "default" => {
  switch (status) {
    case PaymentEventStatus.COMPLETED:
      return "success";
    case PaymentEventStatus.FAILED:
      return "error";
    case PaymentEventStatus.PENDING:
    case PaymentEventStatus.STARTED:
      return "warning";
    default:
      return "default";
  }
};
